import { action, computed, makeObservable, observable } from 'mobx';
import { StoreNode } from '../../store';
import { Store } from '../../store/store';
import { JobModel } from '../../entities';
import { MomentResultItem, MomentResultsCatalog } from '../../pages/searchResultsPage/momentResultsCatalog';
import { JobResultsCatalog } from '../../pages/searchResultsPage/jobResultsCatalog';
import { Metadata } from '@clipr/lib';
import { ApiRequestOptions } from '../../api';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

export class SearchService
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.jobCatalog = new JobResultsCatalog(this.store, { metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
    this.momentCatalog = new MomentResultsCatalog(this.store, { type: 'Moment', metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
    this.topicCatalog = new MomentResultsCatalog(this.store, { type: 'Topic', metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
  }

  readonly jobCatalog: JobResultsCatalog;
  readonly momentCatalog: MomentResultsCatalog;
  readonly topicCatalog: MomentResultsCatalog;

  @observable teamId: string | null = null;

  @computed
  get metadataFilter(): Partial<Metadata> | null {
    return this.store.widgetService.metadataFilter;
  }

  @computed
  get tagsFilter(): string[] | null {
    return this.store.widgetService.tagsFilter;
  }

  @computed
  get showSuggestions(): boolean {
    return this.store.searchResultsPage.showSuggestions;
  }

  @computed
  get jobSuggestions(): JobModel[] {
    return this.jobCatalog.suggestions;
  }

  @computed
  get topicSuggestions(): MomentResultItem[] {
    return this.topicCatalog.suggestions;
  }

  @computed
  get momentSuggestions(): MomentResultItem[] {
    return this.momentCatalog.suggestions;
  }

  @computed
  get hasSuggestions(): boolean {
    return !isEmpty(this.jobCatalog.suggestions) ||
      !isEmpty(this.topicCatalog.suggestions) ||
      !isEmpty(this.momentCatalog.suggestions);
  }

  @computed
  get isLoadingSuggestions(): boolean {
    return this.jobCatalog.isFetchingSuggestions ||
      this.topicCatalog.isFetchingSuggestions ||
      this.momentCatalog.isFetchingSuggestions;
  }

  @computed
  get hasResults(): boolean {
    return !this.jobCatalog.isEmpty || !this.topicCatalog.isEmpty || !this.momentCatalog.isEmpty;
  }

  @computed
  get isLoading(): boolean {
    return this.jobCatalog.isLoading || this.topicCatalog.isLoading || this.momentCatalog.isLoading;
  }

  @action
  async fetchSuggestions(value: string, opts?: ApiRequestOptions) {
    return await Promise.all([
      this.jobCatalog.fetchSuggestions(value, opts),
      this.topicCatalog.fetchSuggestions(value, opts),
      this.momentCatalog.fetchSuggestions(value, opts),
    ]);
  }

  debouncedFetch = debounce(this.fetchSuggestions, 500);

  @action
  clearSuggestions() {
    this.jobCatalog.suggestionsLookup.clear();
    this.topicCatalog.suggestionsLookup.clear();
    this.momentCatalog.suggestionsLookup.clear();
  }

  @action
  handleChange = async (e: { value: string }) => {
    const { value } = e;

    if (value && value.length >= 3) {
      this.debouncedFetch(value);
    } else {
      this.clearSuggestions();
    }
  }

  @action
  async fetchVideos(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.jobCatalog.fetchVideos(value, pageSize, opts);
  }

  @action
  async fetchMoments(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.momentCatalog.fetchMoments(value, pageSize, opts);
  }

  @action
  async fetchTopics(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.topicCatalog.fetchMoments(value, pageSize, opts);
  }

  @action
  async search(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return await Promise.all([
      this.fetchVideos(pageSize, value, opts),
      this.fetchTopics(pageSize, value, opts),
      this.fetchMoments(pageSize, value, opts)
    ]);
  }

  debouncedSearch = debounce(this.search, 500);

  fetchData(pageSize: number, value?: string) {
    if (!this.isLoading) {
      this.search(pageSize, value);
    }

    this.jobCatalog.fetchPopularVideos();
  }

  @action
  setTeamId(teamId: string | null) {
    this.teamId = teamId;
  }

  // TODO: remove direct dependency on pages and decouple from search pages

  @action
  submitToGptPage(replaceRoute = false, teamId: string | null, value?: string | null) {
    this.store.cliprGptSearchResultPageState.submit(replaceRoute, teamId, value);
  }

  @action
  submitToSearchPage(replaceOnSubmit: boolean = false, teamId: string | null) {
    this.store.searchResultsPage.submit(replaceOnSubmit, teamId);
  }

  @action
  loadValueToSearchPage(value?: string | null) {
    this.store.searchResultsPage.loadValue(value);
  }

  @action
  resetSearchResults() {
    this.store.searchResultsPage.reset();
  }

  getSearchModel() {
    return this.store.searchResultsPage.model;
  }
}
